function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { RpcErrorCode } from './constants';
/**
 * Error subclass implementing JSON RPC 2.0 errors and Ethereum RPC errors per EIP-1474.
 * @see https://eips.ethereum.org/EIPS/eip-1474
 */

export class RpcError extends Error {
  constructor(
  /** Number error code */
  code,
  /** Human-readable string */
  message,
  /** Low-level error */
  internal,
  /** Other useful information about error */
  data) {
    if (!Number.isInteger(code)) throw new Error('"code" must be an integer.');
    if (!message || typeof message !== 'string') throw new Error('"message" must be a nonempty string.');
    super(message);

    _defineProperty(this, "code", void 0);

    _defineProperty(this, "data", void 0);

    _defineProperty(this, "internal", void 0);

    this.code = code;
    this.data = data;
    this.internal = internal;
  }

}
/**
 * Error subclass implementing Ethereum Provider errors per EIP-1193.
 * @see https://eips.ethereum.org/EIPS/eip-1193
 */

export class ProviderRpcError extends RpcError {
  /**
   * Create an Ethereum Provider JSON-RPC error.
   * `code` must be an integer in the 1000 <= 4999 range.
   */
  constructor(
  /**
   * Number error code
   * @see https://eips.ethereum.org/EIPS/eip-1193#error-standards
   */
  code,
  /** Human-readable string */
  message,
  /** Low-level error */
  internal,
  /** Other useful information about error */
  data) {
    if (!(Number.isInteger(code) && code >= 1000 && code <= 4999)) throw new Error('"code" must be an integer such that: 1000 <= code <= 4999');
    super(code, message, internal, data);
  }

}
export class AddChainError extends Error {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "name", 'AddChainError');

    _defineProperty(this, "message", 'Error adding chain');
  }

}
export class ChainMismatchError extends Error {
  constructor({
    activeChain,
    targetChain
  }) {
    super(`Chain mismatch: Expected "${targetChain}", received "${activeChain}".`);

    _defineProperty(this, "name", 'ChainMismatchError');
  }

}
export class ChainNotConfiguredError extends Error {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "name", 'ChainNotConfigured');

    _defineProperty(this, "message", 'Chain not configured');
  }

}
export class ConnectorAlreadyConnectedError extends Error {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "name", 'ConnectorAlreadyConnectedError');

    _defineProperty(this, "message", 'Connector already connected');
  }

}
export class ConnectorNotFoundError extends Error {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "name", 'ConnectorNotFoundError');

    _defineProperty(this, "message", 'Connector not found');
  }

}
export class ResourceUnavailableError extends RpcError {
  constructor(error) {
    super(RpcErrorCode.RESOURCE_UNAVAILABLE, 'Resource unavailable', error);

    _defineProperty(this, "name", 'ResourceUnavailable');
  }

}
export class SwitchChainError extends ProviderRpcError {
  constructor(error) {
    super(RpcErrorCode.CHAIN_NOT_FOUND, 'Error switching chain', error);

    _defineProperty(this, "name", 'SwitchChainError');
  }

}
export class SwitchChainNotSupportedError extends Error {
  constructor({
    connector
  }) {
    super(`"${connector.name}" does not support programmatic chain switching.`);

    _defineProperty(this, "name", 'SwitchChainNotSupportedError');
  }

}
export class UserRejectedRequestError extends ProviderRpcError {
  constructor(error) {
    super(4001, 'User rejected request', error);

    _defineProperty(this, "name", 'UserRejectedRequestError');
  }

}
export class ContractMethodDoesNotExistError extends Error {
  constructor({
    addressOrName,
    functionName
  }) {
    super(`Function "${functionName}" on contract "${addressOrName}" does not exist.`);

    _defineProperty(this, "name", 'ContractMethodDoesNotExistError');
  }

}
export class ContractMethodRevertedError extends Error {
  constructor({
    addressOrName,
    args,
    chainId,
    functionName,
    errorMessage
  }) {
    super(['Contract method reverted with an error.', '', `Config:`, JSON.stringify({
      addressOrName,
      contractInterface: '...',
      functionName,
      chainId,
      args
    }, null, 2), '', `Details: ${errorMessage}`].join('\n'));

    _defineProperty(this, "name", 'ContractMethodRevertedError');
  }

}
export class ContractMethodNoResultError extends Error {
  constructor({
    addressOrName,
    args,
    chainId,
    functionName
  }) {
    super(['Contract read returned an empty response. This could be due to any of the following:', `- The contract does not have the function "${functionName}",`, '- The parameters passed to the contract function may be invalid, or', '- The address is not a contract.', '', `Config:`, JSON.stringify({
      addressOrName,
      contractInterface: '...',
      functionName,
      chainId,
      args
    }, null, 2)].join('\n'));

    _defineProperty(this, "name", 'ContractMethodNoResultError');
  }

}
export class ContractResultDecodeError extends Error {
  constructor({
    addressOrName,
    args,
    chainId,
    functionName,
    errorMessage
  }) {
    super(['Failed to decode contract function result.', '', `Config:`, JSON.stringify({
      addressOrName,
      contractInterface: '...',
      functionName,
      chainId,
      args
    }, null, 2), '', `Details: ${errorMessage}`].join('\n'));

    _defineProperty(this, "name", 'ContractResultDecodeError');
  }

}